import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Brain, Heart, ArrowRight, TrendingUp, TrendingDown, Minus, Plus,
  Calendar, Sparkles, Target, ChevronRight, LogOut, ArrowLeft, Lock,
  Crown, Clock, CreditCard, User as UserIcon, Flame,
} from 'lucide-react';
import { toast } from 'sonner';
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { useAuth } from '../../App';
import { API } from '../../App';
import QMGSymbols from '../../components/QMGSymbols';
import { useLockerAccess } from '../../components/SubscriptionGate';
import CoachNoteCard from '../../components/CoachNoteCard';
import FocusPromptList from '../../components/FocusPromptList';
import ScoreVsMentalPanel from '../../components/ScoreVsMentalPanel';
import SuggestMetricPanel from '../../components/SuggestMetricPanel';

const trendIcon = { up: TrendingUp, down: TrendingDown, flat: Minus };
const trendColor = { up: 'text-[#2C5530]', down: 'text-[#8B1E1E]', flat: 'text-[#737373]' };

const PROMPT_KEY = 'qmg.locker.lastDismissed';

const LockerHome = () => {
  const { user, logout } = useAuth();
  const { access } = useLockerAccess();
  const [stats, setStats] = useState(null);
  const [acceptStats, setAcceptStats] = useState(null);
  const [actionStats, setActionStats] = useState(null);
  const [gap, setGap] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [coachNotes, setCoachNotes] = useState([]);
  const [digest, setDigest] = useState(null);
  const [profile, setProfile] = useState(null);
  const [streak, setStreak] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPrompt, setShowPrompt] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [s, list, accept, act, g, notes, dg, prof, st] = await Promise.all([
          API.get('/locker/awareness/stats'),
          API.get('/locker/awareness'),
          API.get('/locker/acceptance/stats'),
          API.get('/locker/action/stats'),
          API.get('/locker/gap'),
          API.get('/locker/coach-notes'),
          API.get('/locker/digest/preview'),
          API.get('/locker/profile'),
          API.get('/locker/streak'),
        ]);
        setStats(s.data);
        setSessions(list.data);
        setAcceptStats(accept.data);
        setActionStats(act.data);
        setGap(g.data);
        setCoachNotes(notes.data);
        setDigest(dg.data);
        setProfile(prof.data);
        setStreak(st.data);
      } catch (err) {
        // ignore — empty state
      } finally {
        setLoading(false);
      }
    })();

    // Auto-prompt once per day
    const today = new Date().toDateString();
    const lastDismissed = localStorage.getItem(PROMPT_KEY);
    if (lastDismissed !== today) {
      setShowPrompt(true);
    }
  }, []);

  const dismissPrompt = () => {
    localStorage.setItem(PROMPT_KEY, new Date().toDateString());
    setShowPrompt(false);
  };

  const [billingLoading, setBillingLoading] = useState(false);
  const openBillingPortal = async () => {
    setBillingLoading(true);
    try {
      const { data } = await API.post('/locker/subscription/manage');
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error('Unable to open billing portal');
        setBillingLoading(false);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Unable to open billing portal');
      setBillingLoading(false);
    }
  };

  const TrendIcon = stats?.trend ? trendIcon[stats.trend] : Minus;

  const chartData = useMemo(() => {
    if (!stats?.series?.length) return [];
    return stats.series.map((s, i) => ({
      i: i + 1,
      label: new Date(s.round_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }),
      score: s.overall,
    }));
  }, [stats]);

  return (
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="locker-home">
      {/* Top bar */}
      <div className="bg-[#1A2E1D] text-white">
        <div className="container mx-auto px-6 md:px-10 py-4 max-w-6xl flex items-center justify-between">
          <Link to="/dashboard" className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm transition-colors" data-testid="back-to-dashboard">
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>
          <div className="flex items-center gap-3 text-sm text-white/80">
            <span className="hidden sm:inline">Hi, {user?.full_name?.split(' ')[0] || 'Player'}</span>
            <Link
              to="/locker/profile"
              className="inline-flex items-center gap-1.5 text-white/70 hover:text-white border border-white/20 hover:border-white/40 px-2.5 py-1 text-xs transition-colors"
              data-testid="open-profile-btn"
            >
              <UserIcon className="w-3.5 h-3.5" /> Profile
            </Link>
            {access?.is_pro && (
              <button
                onClick={openBillingPortal}
                disabled={billingLoading}
                className="inline-flex items-center gap-1.5 text-white/70 hover:text-white border border-white/20 hover:border-white/40 px-2.5 py-1 text-xs transition-colors disabled:opacity-50"
                data-testid="manage-billing-btn"
              >
                <CreditCard className="w-3.5 h-3.5" />
                {billingLoading ? 'Opening…' : 'Manage billing'}
              </button>
            )}
            <button onClick={logout} className="inline-flex items-center gap-1.5 hover:text-white" data-testid="locker-logout">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </div>

      <main className="container mx-auto px-6 md:px-10 py-10 max-w-6xl">
        {/* Hero */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2 flex-wrap">
            <p className="text-[#BC562B] text-xs uppercase tracking-[0.25em]">Player's Locker</p>
            {access?.is_pro && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold bg-[#1A2E1D] text-white px-2 py-1" data-testid="premium-badge">
                <Crown className="w-3 h-3" /> Premium
              </span>
            )}
            {access?.is_trial && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold bg-[#BC562B] text-white px-2 py-1" data-testid="trial-badge">
                <Clock className="w-3 h-3" />
                {access?.days_remaining != null ? `Trial · ${access.days_remaining} day${access.days_remaining === 1 ? '' : 's'} left` : 'Trial'}
              </span>
            )}
            {access?.is_admin && !access?.is_pro && !access?.is_trial && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold bg-[#1A2E1D] text-white px-2 py-1" data-testid="admin-badge">
                <Crown className="w-3 h-3" /> Admin
              </span>
            )}
            {streak?.current_streak > 0 && (
              <span
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold bg-[#FFF4ED] text-[#BC562B] border border-[#BC562B]/30 px-2 py-1"
                data-testid="streak-badge"
                title={`Longest streak: ${streak.longest_streak} days`}
              >
                <Flame className="w-3 h-3" /> {streak.current_streak}-day streak{!streak.today_logged ? ' · log today' : ''}
              </span>
            )}
          </div>
          <h1 className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#1A1A1A] mb-3">Your Mental Performance</h1>
          <p className="text-[#57534E] max-w-2xl leading-relaxed">
            Track how your mind shows up before, during, and after each round. Log it. See the patterns. Train the gap.
          </p>
          <div className="mt-6">
            <QMGSymbols size="sm" tone="dark" />
          </div>

          {/* Current focus goals strip */}
          {profile?.current_focus_goals?.length > 0 && (
            <div className="mt-6 bg-white border border-[#E7E5E4] px-5 py-4" data-testid="focus-goals-strip">
              <div className="flex items-start gap-3">
                <Target className="w-4 h-4 text-[#BC562B] mt-0.5 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#737373] mb-1">Your current focus</p>
                  <ul className="text-sm text-[#1A1A1A] space-y-0.5">
                    {profile.current_focus_goals.map((g, i) => (
                      <li key={i} className="flex items-baseline gap-2" data-testid={`focus-goal-${i}`}>
                        <span className="text-[#BC562B] text-[10px] font-bold">{i + 1}</span>
                        <span>{g}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <Link
                  to="/locker/profile"
                  className="text-xs text-[#737373] hover:text-[#1A2E1D] flex-shrink-0"
                  data-testid="edit-goals-link"
                >
                  Edit
                </Link>
              </div>
            </div>
          )}
        </div>

        {/* Trial ending soon banner */}
        {access?.is_trial && access?.days_remaining != null && access.days_remaining <= 2 && (
          <div className="mb-6 bg-[#1A2E1D] text-white px-5 py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3" data-testid="trial-ending-banner">
            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 text-[#BC562B] mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold text-sm">
                  Your free trial ends in {access.days_remaining} day{access.days_remaining === 1 ? '' : 's'}
                </p>
                <p className="text-sm text-white/70">Subscribe to keep your Locker active.</p>
              </div>
            </div>
            <Link
              to="/locker/paywall"
              className="bg-[#BC562B] text-white text-sm font-semibold px-4 py-2 hover:bg-[#9A4322] transition-colors"
              data-testid="trial-upgrade-btn"
            >
              Subscribe — $99/year
            </Link>
          </div>
        )}

        {/* Daily prompt */}
        {showPrompt && (
          <div className="mb-8 bg-[#BC562B]/10 border border-[#BC562B]/30 px-5 py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3" data-testid="daily-prompt">
            <div className="flex items-start gap-3">
              <Sparkles className="w-5 h-5 text-[#BC562B] mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold text-[#1A1A1A] text-sm">Played or practised today?</p>
                <p className="text-sm text-[#57534E]">Take 2 minutes to log your Awareness — your future self will thank you.</p>
              </div>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <Link
                to="/locker/awareness/new"
                onClick={dismissPrompt}
                className="bg-[#2C5530] text-white text-sm font-semibold px-4 py-2 hover:bg-[#1E3A21] transition-colors"
                data-testid="prompt-log-btn"
              >
                Log it
              </Link>
              <button
                onClick={dismissPrompt}
                className="text-sm text-[#57534E] hover:text-[#1A1A1A] px-3 py-2"
                data-testid="prompt-dismiss-btn"
              >
                Not today
              </button>
            </div>
          </div>
        )}

        {/* Score vs Mental — the north-star tracker */}
        <div className="mb-8">
          <ScoreVsMentalPanel />
        </div>

        {/* Daily focus from coach */}
        <div className="mb-8">
          <FocusPromptList mode="player" />
        </div>

        {/* KPI grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
          {/* Awareness — active */}
          <div className="bg-white border border-[#E7E5E4] p-6 hover:border-[#2C5530] transition-colors" data-testid="kpi-awareness">
            <div className="flex items-start justify-between mb-3">
              <span className="w-10 h-10 rounded-full bg-[#1A2E1D] text-white flex items-center justify-center">
                <Brain className="w-5 h-5" />
              </span>
              {stats?.trend && (
                <span className={`inline-flex items-center gap-1 text-xs font-semibold ${trendColor[stats.trend]}`}>
                  <TrendIcon className="w-3.5 h-3.5" />
                  {stats.trend === 'up' ? 'Improving' : stats.trend === 'down' ? 'Dipping' : 'Steady'}
                </span>
              )}
            </div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#737373] mb-1">Awareness</p>
            <p className="text-4xl font-['Playfair_Display'] text-[#1A1A1A] mb-1">
              {loading ? '—' : (stats?.last_session_score ?? '—')}
              <span className="text-base text-[#737373]"> / 10</span>
            </p>
            <p className="text-xs text-[#57534E] mb-4">
              {stats?.total_sessions ? `${stats.total_sessions} session${stats.total_sessions === 1 ? '' : 's'} logged · avg ${stats.all_time_avg}` : 'No sessions yet'}
            </p>
            <Link
              to="/locker/awareness/new"
              className="inline-flex items-center gap-1 text-[#2C5530] text-sm font-semibold hover:text-[#1E3A21]"
              data-testid="log-awareness-btn"
            >
              <Plus className="w-4 h-4" /> Log a round
            </Link>
          </div>

          {/* Acceptance — active */}
          <div className="bg-white border border-[#E7E5E4] p-6 hover:border-[#BC562B] transition-colors" data-testid="kpi-acceptance">
            <div className="flex items-start justify-between mb-3">
              <span className="w-10 h-10 rounded-full bg-[#BC562B] text-white flex items-center justify-center">
                <Heart className="w-5 h-5" />
              </span>
              {acceptStats?.trend && (
                <span className={`inline-flex items-center gap-1 text-xs font-semibold ${trendColor[acceptStats.trend]}`}>
                  {acceptStats.trend === 'up' ? <TrendingUp className="w-3.5 h-3.5" /> : acceptStats.trend === 'down' ? <TrendingDown className="w-3.5 h-3.5" /> : <Minus className="w-3.5 h-3.5" />}
                  {acceptStats.trend === 'up' ? 'Improving' : acceptStats.trend === 'down' ? 'Dipping' : 'Steady'}
                </span>
              )}
            </div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#737373] mb-1">Acceptance</p>
            <p className="text-4xl font-['Playfair_Display'] text-[#1A1A1A] mb-1">
              {loading ? '—' : (acceptStats?.last_session_score ?? '—')}
              <span className="text-base text-[#737373]"> / 10</span>
            </p>
            <p className="text-xs text-[#57534E] mb-4">
              {acceptStats?.total_sessions ? `${acceptStats.total_sessions} session${acceptStats.total_sessions === 1 ? '' : 's'} · avg ${acceptStats.all_time_avg}` : 'No sessions yet'}
            </p>
            <Link
              to="/locker/acceptance/new"
              className="inline-flex items-center gap-1 text-[#BC562B] text-sm font-semibold hover:text-[#9A4520]"
              data-testid="log-acceptance-btn"
            >
              <Plus className="w-4 h-4" /> Log a round
            </Link>
          </div>

          {/* Action — active */}
          <div className="bg-white border border-[#E7E5E4] p-6 hover:border-[#1A2E1D] transition-colors" data-testid="kpi-action">
            <div className="flex items-start justify-between mb-3">
              <span className="w-10 h-10 rounded-full bg-[#1A2E1D] text-white flex items-center justify-center">
                <ArrowRight className="w-5 h-5" />
              </span>
              {actionStats?.trend && (
                <span className={`inline-flex items-center gap-1 text-xs font-semibold ${trendColor[actionStats.trend]}`}>
                  {actionStats.trend === 'up' ? <TrendingUp className="w-3.5 h-3.5" /> : actionStats.trend === 'down' ? <TrendingDown className="w-3.5 h-3.5" /> : <Minus className="w-3.5 h-3.5" />}
                  {actionStats.trend === 'up' ? 'Improving' : actionStats.trend === 'down' ? 'Dipping' : 'Steady'}
                </span>
              )}
            </div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#737373] mb-1">Action</p>
            <p className="text-4xl font-['Playfair_Display'] text-[#1A1A1A] mb-1">
              {loading ? '—' : (actionStats?.last_session_score ?? '—')}
              <span className="text-base text-[#737373]"> / 10</span>
            </p>
            <p className="text-xs text-[#57534E] mb-4">
              {actionStats?.total_sessions ? `${actionStats.total_sessions} session${actionStats.total_sessions === 1 ? '' : 's'} · avg ${actionStats.all_time_avg}` : 'No sessions yet'}
            </p>
            <Link
              to="/locker/action/new"
              className="inline-flex items-center gap-1 text-[#1A2E1D] text-sm font-semibold hover:text-[#0F1F12]"
              data-testid="log-action-btn"
            >
              <Plus className="w-4 h-4" /> Log a round
            </Link>
          </div>
        </div>

        {/* The Gap + Round Review CTA */}
        <div className="grid md:grid-cols-[1.4fr_1fr] gap-4 mb-10">
          <Link to="/locker/gap" className="bg-[#1A2E1D] text-white p-6 hover:bg-[#0F1F12] transition-colors block" data-testid="kpi-gap">
            <div className="flex items-start justify-between mb-3">
              <span className="text-[10px] uppercase tracking-[0.25em] text-white/60">The Gap</span>
              <Target className="w-5 h-5 text-[#BC562B]" />
            </div>
            <p className="font-['Playfair_Display'] text-4xl tabular-nums mb-1">
              {gap?.latest_gap_aa == null ? '—' : (gap.latest_gap_aa > 0 ? `+${gap.latest_gap_aa}` : gap.latest_gap_aa)}
            </p>
            <p className="text-sm text-white/70">
              {gap?.latest_gap_aa == null
                ? 'Log a round review to surface your Awareness−Action gap.'
                : Math.abs(gap.latest_gap_aa) <= 1
                  ? 'You saw it AND acted on it. Stay here.'
                  : gap.latest_gap_aa > 1
                    ? 'You saw it. The next step is acting on it.'
                    : 'You acted — slow down and notice why.'}
            </p>
          </Link>

          <Link to="/locker/round/new" className="bg-white border border-[#E7E5E4] p-6 hover:border-[#2C5530] transition-colors flex flex-col justify-between" data-testid="kpi-round-cta">
            <div>
              <p className="text-[10px] uppercase tracking-[0.25em] text-[#737373] mb-1">Unified review</p>
              <p className="font-['Playfair_Display'] text-xl text-[#1A1A1A] leading-tight mb-1">Log all 3 KPIs in one flow</p>
              <p className="text-xs text-[#57534E]">Awareness → Acceptance → Action → Notes. Surfaces your Gap instantly.</p>
            </div>
            <span className="mt-3 inline-flex items-center gap-1 text-[#2C5530] text-sm font-semibold">
              <Plus className="w-4 h-4" /> New round review
            </span>
          </Link>
        </div>

        {/* "Your last 30 days" digest preview */}
        {digest && !digest.empty && (
          <div className="bg-white border border-[#E7E5E4] p-6 mb-10" data-testid="digest-preview-panel">
            <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
              <div>
                <p className="text-[#BC562B] text-xs uppercase tracking-[0.25em] mb-1 flex items-center gap-2">
                  <Calendar className="w-3.5 h-3.5" /> Your last 30 days
                </p>
                <h2 className="font-['Playfair_Display'] text-xl md:text-2xl text-[#1A1A1A]">
                  {digest.total_sessions} session{digest.total_sessions === 1 ? '' : 's'} · {digest.rounds_count} round review{digest.rounds_count === 1 ? '' : 's'}
                </h2>
              </div>
              <Link
                to="/locker/gap"
                className="text-xs text-[#737373] hover:text-[#1A2E1D] inline-flex items-center gap-1"
                data-testid="digest-see-more"
              >
                See full analytics <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>

            <div className="grid sm:grid-cols-3 gap-4">
              {/* Top KPI */}
              <div className="bg-[#FAFAF9] border border-[#F2F0E9] p-4" data-testid="digest-top-kpi">
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#737373] mb-1">Top scoring KPI</p>
                {digest.top_kpi ? (
                  <>
                    <p className="font-['Playfair_Display'] text-2xl tabular-nums capitalize" style={{ color: digest.top_kpi[0] === 'awareness' ? '#2C5530' : digest.top_kpi[0] === 'acceptance' ? '#BC562B' : '#1A2E1D' }}>
                      {digest.top_kpi[0]}
                    </p>
                    <p className="text-sm text-[#57534E]">{digest.top_kpi[1]} / 10 avg</p>
                  </>
                ) : (
                  <p className="text-sm text-[#A8A29E]">No sessions yet</p>
                )}
              </div>

              {/* Biggest improvement */}
              <div className="bg-[#FAFAF9] border border-[#F2F0E9] p-4" data-testid="digest-improvement">
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#737373] mb-1">Biggest improvement</p>
                {digest.biggest_improvement && digest.biggest_improvement[1] > 0 ? (
                  <>
                    <p className="font-['Playfair_Display'] text-2xl tabular-nums capitalize" style={{ color: digest.biggest_improvement[0] === 'awareness' ? '#2C5530' : digest.biggest_improvement[0] === 'acceptance' ? '#BC562B' : '#1A2E1D' }}>
                      {digest.biggest_improvement[0]}
                    </p>
                    <p className="text-sm text-[#57534E]">+{digest.biggest_improvement[1]} vs. first half</p>
                  </>
                ) : (
                  <p className="text-sm text-[#A8A29E]">Patterns emerging — keep logging</p>
                )}
              </div>

              {/* Gap trend */}
              <div className="bg-[#FAFAF9] border border-[#F2F0E9] p-4" data-testid="digest-gap">
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#737373] mb-1">The Gap</p>
                {digest.latest_gap_aa != null ? (
                  <>
                    <p className="font-['Playfair_Display'] text-2xl text-[#1A1A1A] tabular-nums">
                      {digest.latest_gap_aa > 0 ? `+${digest.latest_gap_aa}` : digest.latest_gap_aa}
                    </p>
                    <p className="text-sm text-[#57534E] capitalize">{digest.gap_trend ? `${digest.gap_trend}` : 'latest'}</p>
                  </>
                ) : (
                  <p className="text-sm text-[#A8A29E]">Log a unified round to surface it</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Coach notes preview */}
        {coachNotes.length > 0 && (
          <div className="bg-white border border-[#E7E5E4] p-6 mb-10" data-testid="coach-notes-preview">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-['Playfair_Display'] text-xl text-[#1A1A1A]">From your coach</h2>
              <span className="text-xs text-[#737373]">{coachNotes.length} note{coachNotes.length === 1 ? '' : 's'}</span>
            </div>
            <ul className="space-y-3">
              {coachNotes.slice(0, 3).map((n) => (
                <li key={n.id} data-testid={`home-coach-note-${n.id}`}>
                  <CoachNoteCard
                    note={n}
                    currentUserId={user?.id}
                    compact
                    onChange={(updated) => setCoachNotes(coachNotes.map((x) => x.id === updated.id ? updated : x))}
                  />
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Chart + Recent sessions */}
        <div className="grid lg:grid-cols-[1.4fr_1fr] gap-6 mb-10">
          {/* Trend chart */}
          <div className="bg-white border border-[#E7E5E4] p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-[#737373] mb-1">Awareness Trend</p>
                <h2 className="font-['Playfair_Display'] text-xl text-[#1A1A1A]">Last {Math.min(30, chartData.length)} sessions</h2>
              </div>
              <Target className="w-5 h-5 text-[#BC562B]" />
            </div>
            {chartData.length === 0 ? (
              <div className="h-[220px] flex flex-col items-center justify-center text-center" data-testid="empty-chart">
                <Brain className="w-10 h-10 text-[#E7E5E4] mb-3" />
                <p className="text-[#737373] mb-3">No data yet</p>
                <Link to="/locker/awareness/new" className="inline-flex items-center gap-1 text-[#2C5530] text-sm font-semibold hover:text-[#1E3A21]">
                  <Plus className="w-4 h-4" /> Log your first round
                </Link>
              </div>
            ) : (
              <div className="h-[220px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F2F0E9" />
                    <XAxis dataKey="label" stroke="#A8A29E" tick={{ fontSize: 11 }} />
                    <YAxis domain={[0, 10]} stroke="#A8A29E" tick={{ fontSize: 11 }} />
                    <Tooltip
                      contentStyle={{ background: '#1A2E1D', border: 'none', color: 'white', borderRadius: 4 }}
                      formatter={(v) => [`${v} / 10`, 'Awareness']}
                    />
                    <Line type="monotone" dataKey="score" stroke="#2C5530" strokeWidth={2.5} dot={{ r: 4, fill: '#BC562B' }} activeDot={{ r: 6 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* Recent sessions */}
          <div className="bg-white border border-[#E7E5E4] p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-['Playfair_Display'] text-xl text-[#1A1A1A]">Recent rounds</h2>
              <Link to="/locker/awareness" className="text-sm text-[#2C5530] hover:text-[#1E3A21] flex items-center gap-1" data-testid="see-all-link">
                All <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            {sessions.length === 0 ? (
              <div className="text-center py-8" data-testid="empty-sessions">
                <Calendar className="w-10 h-10 text-[#E7E5E4] mx-auto mb-3" />
                <p className="text-[#737373] text-sm">No rounds logged yet</p>
              </div>
            ) : (
              <ul className="space-y-2">
                {sessions.slice(0, 4).map((s) => (
                  <li key={s.id}>
                    <div className="flex items-center justify-between p-3 border border-[#F2F0E9] hover:border-[#2C5530]/40 transition-colors" data-testid={`session-${s.id}`}>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-[#1A1A1A] truncate">{s.course_name || s.round_type || 'Round'}</p>
                        <p className="text-xs text-[#737373]">{new Date(s.round_date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}</p>
                      </div>
                      <span className="bg-[#1A2E1D] text-white text-sm font-semibold px-3 py-1 rounded-sm">
                        {s.overall}/10
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* By round type + sub averages */}
        {stats?.total_sessions > 0 && (
          <div className="grid sm:grid-cols-2 gap-6 mb-10">
            {Object.keys(stats.by_round_type || {}).length > 0 && (
              <div className="bg-white border border-[#E7E5E4] p-6">
                <p className="text-xs uppercase tracking-[0.2em] text-[#737373] mb-1">By round type</p>
                <h3 className="font-['Playfair_Display'] text-lg text-[#1A1A1A] mb-4">Where do you show up?</h3>
                <ul className="space-y-2">
                  {Object.entries(stats.by_round_type).map(([t, v]) => (
                    <li key={t} className="flex items-center justify-between text-sm">
                      <span className="capitalize text-[#1A1A1A]">{t}</span>
                      <span className="font-semibold text-[#1A2E1D]">{v} / 10</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {Object.values(stats.sub_score_averages || {}).some((v) => v !== null) && (
              <div className="bg-white border border-[#E7E5E4] p-6">
                <p className="text-xs uppercase tracking-[0.2em] text-[#737373] mb-1">Awareness indicators</p>
                <h3 className="font-['Playfair_Display'] text-lg text-[#1A1A1A] mb-4">Detailed averages</h3>
                <ul className="space-y-2">
                  {Object.entries(stats.sub_score_averages).map(([k, v]) => (
                    v !== null && (
                      <li key={k} className="flex items-center justify-between text-sm">
                        <span className="capitalize text-[#1A1A1A]">{k}</span>
                        <span className="font-semibold text-[#1A2E1D]">{v} / 10</span>
                      </li>
                    )
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Suggest a metric / improvement */}
        <div className="mb-10">
          <SuggestMetricPanel />
        </div>
      </main>
    </div>
  );
};

export default LockerHome;
